<template>
  <div class="review-config">
    <!-- 左侧：审批类型列表 -->
    <div class="rc-tree">
      <div class="left-toolbar">
        <a-input-search v-model="keyword" placeholder="搜索类型" allow-clear class="left-search" />
        <a-checkbox v-model="showDisabled" class="left-check">显示已停用</a-checkbox>
        <a-tooltip title="新增审批类型">
          <a-button type="primary" shape="circle" icon="plus" @click="openAddCat" />
        </a-tooltip>
      </div>
      <div class="type-list">
        <!-- 按所属模块→所属分类→审批类型三级分组展示 -->
        <div v-for="group in groupedTypes" :key="group.module" class="module-group">
          <div class="module-title" @click="toggleModule(group.module)">
            <a-icon :type="isModuleCollapsed(group.module) ? 'caret-right' : 'caret-down'" class="caret" />
            <span class="group-name" :title="group.module">{{ group.module }}</span>
            <span class="group-count">{{ group.count }}</span>
          </div>
          <template v-if="!isModuleCollapsed(group.module) || isSearching">
            <div
              v-for="cat in group.categories"
              :key="group.module + '/' + cat.category"
              class="cat-block"
            >
              <div class="cat-title" :title="cat.category">{{ cat.category }}</div>
              <div
                v-for="item in cat.types"
                :key="item.id"
                class="type-item type-item-indent"
                :class="{ active: current && current.id === item.id, disabled: item.status === false }"
                @click="selectType(item)"
              >
                <div class="type-name">
                  <span class="type-name-text">{{ item.taskName || item.taskType }}</span>
                  <a-tag v-if="item.status === false" color="default" class="type-tag">已停用</a-tag>
                </div>
                <div class="type-key">{{ item.taskType }}</div>
              </div>
            </div>
          </template>
        </div>
        <a-empty v-if="groupedTypes.length === 0" :description="'无匹配类型'" />
      </div>
      <div class="type-count">共 {{ filteredTypes.length }} 个审批类型</div>
    </div>

    <!-- 右侧 -->
    <div class="rc-detail">
      <template v-if="current">
        <!-- 右上：审批类型全局配置 -->
        <div class="panel-header">
          <div class="panel-title">
            {{ current.taskName || current.taskType }}
            <span class="right-key">{{ current.taskType }}</span>
            <a-tag v-if="current.status === false" color="default">已停用</a-tag>
          </div>
          <div class="panel-desc">审批类型全局配置：工作流开关、起草中心置顶与基础信息</div>
          <div class="panel-actions">
            <span class="switch-item">
              <span class="switch-label">工作流</span>
              <a-switch size="small" :checked="current.isWorkflow" @change="(v) => toggleFlag(current, 'isWorkflow', v)" />
            </span>
            <span class="switch-item">
              <span class="switch-label">起草中心置顶</span>
              <a-switch size="small" :checked="current.isTop" @change="(v) => toggleFlag(current, 'isTop', v)" />
            </span>
            <a-divider type="vertical" />
            <a-button type="primary" :loading="catSaving" @click="saveCategory">保存配置</a-button>
            <a-button v-if="current.status === false" type="primary" ghost @click="enableCategory">恢复启用</a-button>
            <a-button v-else type="danger" @click="removeCategory">删除类型</a-button>
          </div>
        </div>
        <a-form-model layout="vertical" class="cat-form">
          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-model-item label="类型名称">
                <a-input v-model="catForm.taskName" placeholder="审批类型名称" allow-clear />
              </a-form-model-item>
            </a-col>
            <a-col :span="8">
              <a-form-model-item label="所属模块">
                <!-- 下拉提供数据库已有模块，无匹配可手动输入；变化时清空分类保持从属一致 -->
                <a-auto-complete
                  v-model="catForm.module"
                  :data-source="moduleOptions"
                  placeholder="如：oa / activiti"
                  allow-clear
                  @change="onEditModuleChange"
                />
              </a-form-model-item>
            </a-col>
            <a-col :span="8">
              <a-form-model-item label="分类">
                <a-auto-complete
                  v-model="catForm.category"
                  :data-source="editCategoryOptions"
                  placeholder="分类标识"
                  allow-clear
                />
              </a-form-model-item>
            </a-col>
            <a-col :span="8">
              <a-form-model-item label="排序">
                <a-input-number v-model="catForm.sort" :min="0" style="width: 100%" />
              </a-form-model-item>
            </a-col>
            <a-col :span="8">
              <a-form-model-item label="路径">
                <a-input v-model="catForm.path" placeholder="关联页面路径（可选）" allow-clear />
              </a-form-model-item>
            </a-col>
            <a-col :span="8">
              <a-form-model-item label="类型编码">
                <a-input :value="current.taskType" disabled />
              </a-form-model-item>
            </a-col>
          </a-row>
        </a-form-model>

        <a-divider class="block-divider" />

        <!-- 右下：节点配置表格 -->
        <div class="panel-header">
          <div class="panel-title-row">
            <div class="panel-title">节点配置</div>
            <a-button type="primary" icon="plus" @click="openAddNode">新增节点</a-button>
          </div>
          <div class="panel-desc">维护该类型下的节点变量，并绑定岗位或直配人员</div>
        </div>
        <a-table
          bordered
          rowKey="id"
          size="middle"
          :dataSource="dictList"
          :loading="dictLoading"
          :pagination="false"
        >
            <a-table-column title="节点名称" data-index="nodeName">
              <template slot-scope="text">
                {{ text || "--" }}
              </template>
            </a-table-column>
            <a-table-column title="节点变量" data-index="role" />
            <a-table-column title="参与方式" :width="90">
              <template slot-scope="text">
                <a-tag v-if="text.multiParticipant" color="blue">多人</a-tag>
                <a-tag v-else>单人</a-tag>
              </template>
            </a-table-column>
            <a-table-column title="绑定岗位">
              <template slot-scope="text">
                <template v-if="text.positionId">
                  <a-tag color="green">{{ text.positionName || text.positionId }}</a-tag>
                  <span class="count" :class="{ 'count-warn': !text.multiParticipant && text.positionUserCount !== 1 }">
                    {{ text.positionUserCount }} 人
                  </span>
                </template>
                <span v-else class="hint">未绑定（人员直配）</span>
              </template>
            </a-table-column>
            <a-table-column title="人员">
              <template slot-scope="text">
                <span v-if="text.positionId" class="hint">运行时取岗位人员</span>
                <span v-else>{{ text.userName || "--" }}</span>
              </template>
            </a-table-column>
            <a-table-column title="知会" :width="70">
              <template slot-scope="text">
                <a-tooltip v-if="notifyNames(text).length">
                  <template slot="title">
                    <div v-for="(n, i) in notifyNames(text)" :key="i">{{ n }}</div>
                  </template>
                  <a-badge :count="notifyNames(text).length" :number-style="{ backgroundColor: '#52c41a' }">
                    <a-icon type="notification" style="font-size: 15px" />
                  </a-badge>
                </a-tooltip>
                <span v-else class="hint">--</span>
              </template>
            </a-table-column>
            <a-table-column title="操作" :width="200">
              <template slot-scope="text">
                <a-button type="link" size="small" @click="openBindModal(text)">
                  {{ text.positionId ? "换绑岗位" : "绑定岗位" }}
                </a-button>
                <a-button v-if="text.positionId" type="link" size="small" @click="unbind(text)">解绑</a-button>
                <a-button type="link" size="small" @click="openUsersModal(text)">编辑人员</a-button>
                <a-button type="link" size="small" class="danger-link" @click="removeNode(text)">删除</a-button>
              </template>
            </a-table-column>
          </a-table>
      </template>
      <a-empty v-else description="请在左侧选择审批类型" style="margin-top: 100px" />
    </div>

    <!-- 新增审批类型对话框 -->
    <a-modal
      :visible="addCatVisible"
      title="新增审批类型"
      :confirmLoading="addCatSaving"
      width="520px"
      @ok="doAddCat"
      @cancel="addCatVisible = false"
    >
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item label="类型编码" required>
          <a-input v-model="addCatForm.taskType" placeholder="唯一编码，如 contract_review" allow-clear />
        </a-form-model-item>
        <a-form-model-item label="类型名称">
          <a-input v-model="addCatForm.taskName" placeholder="审批类型名称" allow-clear />
        </a-form-model-item>
        <a-form-model-item label="所属模块">
          <!-- 下拉提供数据库已有模块，无匹配可手动输入；变化时清空分类保持从属一致 -->
          <a-auto-complete
            v-model="addCatForm.module"
            :data-source="moduleOptions"
            placeholder="如：oa / activiti"
            allow-clear
            @change="onAddModuleChange"
          />
        </a-form-model-item>
        <a-form-model-item label="分类">
          <a-auto-complete
            v-model="addCatForm.category"
            :data-source="addCategoryOptions"
            placeholder="分类标识"
            allow-clear
          />
        </a-form-model-item>
        <a-form-model-item label="排序">
          <a-input-number v-model="addCatForm.sort" :min="0" style="width: 100%" />
        </a-form-model-item>
        <a-form-model-item label="路径">
          <a-input v-model="addCatForm.path" placeholder="关联页面路径（可选）" allow-clear />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 新增节点对话框 -->
    <a-modal
      :visible="addNodeVisible"
      title="新增审批节点"
      :confirmLoading="addNodeSaving"
      width="560px"
      @ok="doAddNode"
      @cancel="addNodeVisible = false"
    >
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item label="所属类型">
          <span class="bind-node">{{ current ? `${current.taskName || current.taskType}（${current.taskType}）` : "" }}</span>
        </a-form-model-item>
        <a-form-model-item label="节点变量" required>
          <a-input v-model="addNodeForm.role" placeholder="与bpmn定义一致的变量名，如 dept_leader" allow-clear />
        </a-form-model-item>
        <a-form-model-item label="节点名称">
          <a-input v-model="addNodeForm.nodeName" placeholder="如：部门负责人审批" allow-clear />
        </a-form-model-item>
        <a-form-model-item label="多人参与">
          <a-switch v-model="addNodeForm.multiParticipant" />
          <span class="switch-tip" style="margin-left: 8px">开启后岗位可绑定多人；单人节点绑定的岗位必须恰好1人</span>
        </a-form-model-item>
        <a-form-model-item label="人员ID">
          <a-textarea v-model="addNodeForm.userId" :rows="2" placeholder="直配模式：用户uuid，多个用英文逗号分割（可选）" />
        </a-form-model-item>
        <a-form-model-item label="人员名称">
          <a-textarea v-model="addNodeForm.userName" :rows="2" placeholder="姓名，多个用英文逗号分割（与ID顺序对应）" />
        </a-form-model-item>
        <a-form-model-item label="知会extra">
          <a-textarea v-model="addNodeForm.extra" :rows="3" placeholder='JSON数组，如 [{"name":"张三","userId":"xxx"}]（可选）' />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 绑定岗位对话框 -->
    <a-modal
      :visible="bindVisible"
      title="绑定审批岗位"
      :confirmLoading="bindSaving"
      width="520px"
      @ok="doBind"
      @cancel="bindVisible = false"
    >
      <a-alert
        v-if="bindRow && !bindRow.multiParticipant"
        type="warning"
        show-icon
        message="该节点为单人参与，所选岗位必须恰好 1 人"
        style="margin-bottom: 16px"
      />
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item label="节点变量">
          <span class="bind-node">{{ bindRow ? `${bindRow.nodeName || "--"}（${bindRow.role}）` : "" }}</span>
        </a-form-model-item>
        <a-form-model-item label="选择岗位">
          <a-select
            v-model="bindPositionId"
            show-search
            option-filter-prop="children"
            placeholder="请选择岗位"
            allow-clear
            @change="onBindPositionChange"
          >
            <a-select-option v-for="p in positionList" :key="p.id" :value="p.id">
              {{ p.name }}<span v-if="p.deptName" class="option-dept">（{{ p.deptName }}）</span>
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="岗位人员">
          <template v-if="bindPositionId">
            <div v-if="bindUsers.length" class="bind-users">
              <a-tag v-for="u in bindUsers" :key="u.userId" color="green">{{ u.userName }}</a-tag>
            </div>
            <div v-else class="warn-text">该岗位当前无有效人员</div>
          </template>
          <span v-else class="hint">选择岗位后显示人员预览</span>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 编辑直配人员对话框 -->
    <a-modal
      :visible="usersVisible"
      title="编辑直配人员"
      :confirmLoading="usersSaving"
      width="560px"
      @ok="doSaveUsers"
      @cancel="usersVisible = false"
    >
      <a-alert
        type="info"
        show-icon
        message="绑定岗位的节点运行时自动取岗位人员（user_id/user_name 已回填快照）；此编辑仅对未绑定岗位的节点生效"
        style="margin-bottom: 16px"
      />
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item label="节点变量">
          <span class="bind-node">{{ usersRow ? `${usersRow.nodeName || "--"}（${usersRow.role}）` : "" }}</span>
        </a-form-model-item>
        <a-form-model-item label="人员ID">
          <a-textarea v-model="usersForm.userId" :rows="2" placeholder="用户uuid，多个用英文逗号分割" />
        </a-form-model-item>
        <a-form-model-item label="人员名称">
          <a-textarea v-model="usersForm.userName" :rows="2" placeholder="姓名，多个用英文逗号分割（与ID顺序对应）" />
        </a-form-model-item>
        <a-form-model-item label="知会extra">
          <a-textarea v-model="usersForm.extra" :rows="4" placeholder='JSON数组，如 [{"name":"张三","userId":"xxx"}]' />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import {
    fetchReviewCategoryList,
    updateReviewCategoryExtra,
    addReviewCategory,
    updateReviewCategory,
    deleteReviewCategory,
    enableReviewCategory,
    fetchReviewDictList,
    addReviewDict,
    deleteReviewDict,
    bindReviewDictPosition,
    unbindReviewDictPosition,
    updateReviewDictUsers,
    fetchPositionUsersBatch,
    fetchPositionList,
} from "@/api/setting/auth";

export default {
    name: "ReviewConfig",

    data() {
        return {
            keyword: "",
            showDisabled: false,
            types: [],
            current: null,
            // 左侧树折叠状态（对象当Set用，key=模块名）
            collapsedModules: {},

            // 右上：类型配置表单
            catForm: { taskName: "", module: "", category: undefined, sort: 999, path: "" },
            catSaving: false,

            dictList: [],
            dictLoading: false,

            // 新增类型
            addCatVisible: false,
            addCatSaving: false,
            addCatForm: { taskType: "", taskName: "", module: "", category: undefined, sort: 999, path: "" },

            // 新增节点
            addNodeVisible: false,
            addNodeSaving: false,
            addNodeForm: { role: "", nodeName: "", multiParticipant: false, userId: "", userName: "", extra: "" },

            bindVisible: false,
            bindSaving: false,
            bindRow: null,
            bindPositionId: undefined,
            bindUsers: [],
            positionList: [],

            usersVisible: false,
            usersSaving: false,
            usersRow: null,
            usersForm: { userId: "", userName: "", extra: "" },
        };
    },

    computed: {
        filteredTypes() {
            const kw = (this.keyword || "").trim().toLowerCase();
            let list = this.types;
            if (!this.showDisabled) {
                list = list.filter((t) => t.status !== false);
            }
            if (!kw) {
                return list;
            }
            return list.filter(
                (t) =>
                    (t.taskName || "").toLowerCase().includes(kw) ||
                    (t.taskType || "").toLowerCase().includes(kw) ||
                    (t.category || "").toLowerCase().includes(kw)
            );
        },

        isSearching() {
            return !!(this.keyword || "").trim();
        },

        // 按所属模块→所属分类两级分组，类型为叶子；模块/分类为空归入"未分组/未分类"且排最后
        groupedTypes() {
            const moduleMap = new Map();
            this.filteredTypes.forEach((t) => {
                const module = t.module || "未分组";
                if (!moduleMap.has(module)) {
                    moduleMap.set(module, new Map());
                }
                const catMap = moduleMap.get(module);
                const category = t.category || "未分类";
                if (!catMap.has(category)) {
                    catMap.set(category, []);
                }
                catMap.get(category).push(t);
            });
            const sortedModules = [...moduleMap.keys()].sort(
                (a, b) => (a === "未分组") - (b === "未分组") || a.localeCompare(b)
            );
            return sortedModules.map((module) => {
                const catMap = moduleMap.get(module);
                const sortedCats = [...catMap.keys()].sort(
                    (a, b) => (a === "未分类") - (b === "未分类") || a.localeCompare(b)
                );
                const categories = sortedCats.map((category) => ({
                    category,
                    types: catMap.get(category),
                }));
                return {
                    module,
                    categories,
                    count: categories.reduce((sum, c) => sum + c.types.length, 0),
                };
            });
        },

        // 数据库已有的模块，供新增与编辑下拉选择（auto-complete仍允许手动输入）
        moduleOptions() {
            return [...new Set(this.types.map((t) => t.module).filter(Boolean))].sort((a, b) =>
                a.localeCompare(b)
            );
        },
        // 分类下拉跟随所属模块联动：选了模块只显示该模块下已有分类，未选模块显示全量
        addCategoryOptions() {
            return this.categoryOptionsOf(this.addCatForm.module);
        },
        editCategoryOptions() {
            return this.categoryOptionsOf(this.catForm.module);
        },
    },

    mounted() {
        this.loadTypes();
        fetchPositionList()
            .then((res) => {
                this.positionList = Array.isArray(res) ? res : [];
            })
            .catch(() => {});
    },

    methods: {
        loadTypes() {
            fetchReviewCategoryList().then((res) => {
                if (Array.isArray(res)) {
                    this.types = res;
                    if (!this.current && res.length) {
                        const firstEnabled = res.find((t) => t.status !== false) || res[0];
                        this.selectType(firstEnabled);
                    } else if (this.current) {
                        const fresh = res.find((t) => t.id === this.current.id);
                        if (fresh) {
                            this.current = fresh;
                            this.syncCatForm();
                        }
                    }
                }
            });
        },

        selectType(item) {
            this.current = item;
            this.syncCatForm();
            this.loadDict();
        },

        toggleModule(module) {
            // Vue2对象新增键需整体替换保证响应式
            this.collapsedModules = { ...this.collapsedModules, [module]: !this.collapsedModules[module] };
        },

        isModuleCollapsed(module) {
            return !!this.collapsedModules[module];
        },

        // 指定模块下已有的分类（module为空时返回全量分类）
        categoryOptionsOf(module) {
            const source = module
                ? this.types.filter((t) => t.module === module)
                : this.types;
            return [...new Set(source.map((t) => t.category).filter(Boolean))].sort((a, b) =>
                a.localeCompare(b)
            );
        },

        // 所属模块变化时清空已选分类，保证分类与模块从属一致
        onAddModuleChange() {
            this.addCatForm.category = undefined;
        },
        onEditModuleChange() {
            this.catForm.category = undefined;
        },

        syncCatForm() {
            if (!this.current) {
                return;
            }
            this.catForm = {
                taskName: this.current.taskName || "",
                module: this.current.module || "",
                category: this.current.category,
                sort: this.current.sort == null ? 999 : this.current.sort,
                path: this.current.path || "",
            };
        },

        // ===================== 审批类型 =====================

        toggleFlag(item, field, value) {
            const payload = { id: item.id };
            payload.isWorkflow = field === "isWorkflow" ? value : item.isWorkflow;
            payload.isTop = field === "isTop" ? value : item.isTop;
            updateReviewCategoryExtra(payload).then(() => {
                item[field] = value;
                this.$message.success(`已${value ? "开启" : "关闭"}${field === "isWorkflow" ? "工作流" : "起草中心置顶"}`);
            });
        },

        saveCategory() {
            if (!this.catForm.taskName || !this.catForm.taskName.trim()) {
                this.$message.warning("请填写类型名称");
                return;
            }
            this.catSaving = true;
            updateReviewCategory({ id: this.current.id, ...this.catForm })
                .then(() => {
                    this.catSaving = false;
                    this.$message.success("保存成功");
                    this.loadTypes();
                })
                .catch(() => {
                    this.catSaving = false;
                });
        },

        removeCategory() {
            const that = this;
            this.$confirm({
                title: `确认删除审批类型【${this.current.taskName || this.current.taskType}】？`,
                content: "删除为软删除（置 status=false），类型将从工作台审批起草中心下架，可在「显示已停用」中恢复",
                onOk() {
                    return deleteReviewCategory(that.current.id).then(() => {
                        that.$message.success("删除成功");
                        that.loadTypes();
                    });
                },
            });
        },

        enableCategory() {
            const that = this;
            enableReviewCategory(this.current.id).then(() => {
                that.$message.success("已恢复启用");
                that.loadTypes();
            });
        },

        openAddCat() {
            this.addCatForm = { taskType: "", taskName: "", module: "", category: undefined, sort: 999, path: "" };
            this.addCatVisible = true;
        },

        doAddCat() {
            if (!this.addCatForm.taskType || !this.addCatForm.taskType.trim()) {
                this.$message.warning("请填写类型编码");
                return;
            }
            this.addCatSaving = true;
            addReviewCategory(this.addCatForm)
                .then(() => {
                    this.addCatSaving = false;
                    this.addCatVisible = false;
                    this.$message.success("新增成功");
                    this.loadTypes();
                })
                .catch(() => {
                    this.addCatSaving = false;
                });
        },

        // ===================== 审批节点 =====================

        loadDict() {
            if (!this.current) {
                return;
            }
            this.dictLoading = true;
            fetchReviewDictList({ type: this.current.taskType })
                .then((res) => {
                    this.dictLoading = false;
                    if (Array.isArray(res)) {
                        this.dictList = res;
                    }
                })
                .catch(() => {
                    this.dictLoading = false;
                });
        },

        openAddNode() {
            this.addNodeForm = { role: "", nodeName: "", multiParticipant: false, userId: "", userName: "", extra: "" };
            this.addNodeVisible = true;
        },

        doAddNode() {
            if (!this.addNodeForm.role || !this.addNodeForm.role.trim()) {
                this.$message.warning("请填写节点变量");
                return;
            }
            this.addNodeSaving = true;
            addReviewDict({ type: this.current.taskType, ...this.addNodeForm })
                .then(() => {
                    this.addNodeSaving = false;
                    this.addNodeVisible = false;
                    this.$message.success("新增成功");
                    this.loadDict();
                })
                .catch(() => {
                    this.addNodeSaving = false;
                });
        },

        removeNode(row) {
            if (row.positionId) {
                this.$message.warning("节点已绑定岗位，请先解绑再删除");
                return;
            }
            const that = this;
            this.$confirm({
                title: `确认删除节点【${row.nodeName || row.role}】？`,
                content: "删除为软删除，删除后不再参与审批人员解析",
                onOk() {
                    return deleteReviewDict(row.id).then(() => {
                        that.$message.success("删除成功");
                        that.loadDict();
                    });
                },
            });
        },

        notifyNames(row) {
            if (!row.extra) {
                return [];
            }
            try {
                const arr = JSON.parse(row.extra);
                if (Array.isArray(arr)) {
                    return arr.map((o) => o.name).filter((n) => !!n);
                }
                return [];
            } catch (e) {
                return [];
            }
        },

        openBindModal(row) {
            this.bindRow = row;
            this.bindPositionId = row.positionId || undefined;
            this.bindUsers = [];
            this.bindVisible = true;
            if (row.positionId) {
                this.onBindPositionChange(row.positionId);
            }
        },

        onBindPositionChange(positionId) {
            this.bindUsers = [];
            if (!positionId) {
                return;
            }
            fetchPositionUsersBatch({ positionIds: positionId }).then((res) => {
                if (Array.isArray(res) && res.length) {
                    this.bindUsers = res[0].users || [];
                }
            });
        },

        doBind() {
            if (!this.bindPositionId) {
                this.$message.warning("请选择岗位");
                return;
            }
            // 前端拦截：单人参与节点，岗位人数必须恰好1人（后端同样校验，双保险）
            if (this.bindRow && !this.bindRow.multiParticipant && this.bindUsers.length !== 1) {
                this.$message.warning(
                    `该节点为单人参与，所选岗位当前有效人员为 ${this.bindUsers.length} 人，必须恰好1人才能绑定`
                );
                return;
            }
            const pos = this.positionList.find((p) => p.id === this.bindPositionId);
            this.bindSaving = true;
            bindReviewDictPosition({
                id: this.bindRow.id,
                positionId: this.bindPositionId,
                positionName: pos ? pos.name : "",
            })
                .then(() => {
                    this.bindSaving = false;
                    this.bindVisible = false;
                    this.$message.success("绑定成功，人员快照已回填");
                    this.loadDict();
                })
                .catch(() => {
                    this.bindSaving = false;
                });
        },

        unbind(row) {
            const that = this;
            this.$confirm({
                title: `确认解绑岗位【${row.positionName}】？`,
                content: "解绑后该节点回到人员直配模式（保留绑定期间回填的人员快照）",
                onOk() {
                    unbindReviewDictPosition({ id: row.id }).then(() => {
                        that.$message.success("解绑成功");
                        that.loadDict();
                    });
                },
            });
        },

        openUsersModal(row) {
            this.usersRow = row;
            this.usersForm = {
                userId: row.userId || "",
                userName: row.userName || "",
                extra: row.extra || "",
            };
            this.usersVisible = true;
        },

        doSaveUsers() {
            this.usersSaving = true;
            updateReviewDictUsers({
                id: this.usersRow.id,
                userId: this.usersForm.userId,
                userName: this.usersForm.userName,
                extra: this.usersForm.extra,
            })
                .then(() => {
                    this.usersSaving = false;
                    this.usersVisible = false;
                    this.$message.success("保存成功");
                    this.loadDict();
                })
                .catch(() => {
                    this.usersSaving = false;
                });
        },
    },
};
</script>

<style scoped>
.review-config {
    display: flex;
    gap: 16px;
}

/* 左侧类型列表 */
.rc-tree {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.left-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
}
.left-search {
    flex: 1;
    min-width: 0;
}
.left-check {
    flex-shrink: 0;
    font-size: 12px;
    white-space: nowrap;
}
.type-tag {
    flex-shrink: 0;
    margin-left: 6px;
    transform: scale(0.85);
    transform-origin: right center;
}
.type-item.disabled .type-name-text {
    color: rgba(0, 0, 0, 0.35);
}
/* 左侧分组树：模块标题/分类标题 */
.module-group {
    margin-bottom: 2px;
}
.module-title {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 8px;
    cursor: pointer;
    border-radius: 4px;
    font-weight: 600;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    background: #fafafa;
    user-select: none;
}
.module-title:hover {
    background: #f0f0f0;
}
.module-title .caret {
    font-size: 10px;
    color: rgba(0, 0, 0, 0.45);
}
.group-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.group-count {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
    background: #f0f0f0;
    border-radius: 9px;
    padding: 0 7px;
    line-height: 18px;
}
.cat-block {
    margin-top: 2px;
}
.cat-title {
    padding: 3px 8px 3px 22px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: none;
}
.type-item-indent {
    margin-left: 14px;
    padding: 6px 10px;
}
.type-list {
    margin-top: 8px;
    flex: 1;
    max-height: 620px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
}
.type-item {
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 4px;
    cursor: pointer;
    transition: all 0.2s;
    overflow: hidden;
}
.type-item:hover {
    background: #f5f5f5;
}
.type-item.active {
    background: #e6f7ff;
    box-shadow: inset 3px 0 0 #1890ff;
}
.type-name {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.85);
    line-height: 1.4;
}
.type-name-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.type-key {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.type-count {
    margin-top: 8px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
    text-align: center;
}

/* 右侧：审批类型全局配置在上，节点表格在下 */
.rc-detail {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}
.panel-header {
    margin-bottom: 16px;
}
.panel-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
}
.panel-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}
.panel-title {
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    line-height: 1.5;
}
.panel-desc {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 2px;
}
.block-divider {
    margin: 4px 0 20px;
}
.right-key {
    font-size: 12px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
    background: #f5f5f5;
    padding: 1px 8px;
    border-radius: 10px;
    margin-left: 8px;
}
.cat-form {
    margin-top: 4px;
}
.switch-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.switch-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
}
.switch-tip {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
}
.count {
    margin-left: 4px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
}
.count-warn {
    color: #fa541c;
    font-weight: 600;
}
.hint {
    color: rgba(0, 0, 0, 0.4);
    font-size: 12px;
}
.warn-text {
    color: #faad14;
    font-size: 12px;
}
.bind-node {
    color: rgba(0, 0, 0, 0.85);
}
.option-dept {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}
.bind-users {
    margin-bottom: 4px;
}
.danger-link {
    color: #f5222d;
}
</style>
